import { cn } from '@/utils';
import React, { useEffect, useRef } from 'react';

// Highcharts is loaded once by src/vendor.js, which attaches the highcharts-more,
// exporting and accessibility modules to that instance and publishes it globally.
// `vendor` and `app` are separate rspack entries with no shared chunks, so
// `import Highcharts from 'highcharts'` here would bundle a second copy of the
// library with none of those modules applied. Use the global, as the rest of the
// app does.
//
// `options` is an effect dependency and is applied with `chart.update` on every
// new object identity — memoise it on the caller's side, or the chart redraws on
// every render.
export function HighchartsChart({ className, options }) {
    const
        chart = useRef(null),
        container = useRef(null),
        // Also the options the chart is currently built with, so a remount
        // rebuilds from the latest ones rather than the first ones.
        appliedOptions = useRef(options);

    useEffect(() => {
        // Built once. Rebuilding on every change would throw away the chart's own
        // state — hovered point, hidden series, zoom — so changes go through
        // `chart.update` below instead.
        chart.current = Highcharts.chart(container.current, appliedOptions.current);

        return () => {
            chart.current?.destroy();
            chart.current = null;
        };
    }, []);

    useEffect(() => {
        // Skips the first run: the chart was just built with these options.
        if (appliedOptions.current === options) {
            return;
        }

        appliedOptions.current = options;

        // `oneToOne` so that dropping a series from the options drops it from the
        // chart, instead of leaving the old one behind.
        chart.current?.update(options, true, true);
    }, [options]);

    useEffect(() => {
        // GridStack resizes a widget without firing a window resize, so Highcharts
        // never learns its container changed and keeps the size it was built at.
        const observer = new ResizeObserver(() => chart.current?.reflow());

        observer.observe(container.current);

        return () => observer.disconnect();
    }, []);

    return <div className={cn('tw-h-full tw-w-full', className)} ref={container}/>;
}
